"use client";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { navLinks } from "@/data/main-data";
import { MenuProps } from "@/types/main-types";
import PropertyButton from "./PropertyButton";

export default function AppMenu({ pathname, setShow }: MenuProps) {
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  const t = useTranslations("Nav");

  const isArabic = locale === "ar";

  return (
    <div className="relative xl:hidden">
      <button
        onClick={() => setOpen(true)}
        className={`p-2 hover:text-main transition cursor-pointer ${
          pathname ? "text-white" : "text-gray-700"
        }`}
      >
        <Menu size={28} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            <motion.aside
              className={`fixed top-0 ${
                isArabic ? "right-0" : "left-0"
              } h-full w-72 bg-white shadow-2xl z-50 p-6 flex flex-col ${
                isArabic ? "text-right" : "text-left"
              }`}
              initial={{ x: isArabic ? 300 : -300 }}
              animate={{ x: 0 }}
              exit={{ x: isArabic ? 300 : -300 }}
              transition={{ type: "spring", stiffness: 90, damping: 15 }}
            >
              <button
                onClick={() => setOpen(false)}
                className={`absolute top-4 ${
                  isArabic ? "left-4" : "right-4"
                } text-gray-700 hover:text-main`}
              >
                <X size={26} />
              </button>

              <nav className="mt-12 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`block py-2 px-4 rounded-md text-lg font-medium transition-colors duration-300 ${
                      link.href === ""
                        ? "bg-main text-white"
                        : "text-gray-700 hover:bg-main/10 hover:text-main"
                    } ${
                      link.href === "/" && pathname ? "bg-main text-white" : ""
                    }`}
                    onClick={() => {
                      setOpen(false);
                      setShow(false);
                    }}
                  >
                    {t(link.label)}
                  </Link>
                ))}
                <div className="mt-8 xm:hidden">
                  <PropertyButton isHome={pathname} />
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
